<template>
  <div class="bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/50 rounded-xl p-4 mb-4">
    <div class="mb-3">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
        <svg class="w-4 h-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
        </svg>
        Realtime Session Control
      </h3>
      <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
        Select who controls the active song and transposition for all viewers in real time.
      </p>
    </div>

    <!-- Controller Options List -->
    <div class="space-y-2 max-h-[160px] overflow-y-auto pr-1">
      <div 
        v-for="user in eligibleUsers" 
        :key="user.id"
        @click="selectController(user.id)"
        class="flex items-center justify-between p-2.5 rounded-lg border transition-all"
        :class="[
          isActiveController(user.id) 
            ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/20' 
            : 'border-gray-100 dark:border-gray-700/50 hover:bg-gray-100/50 dark:hover:bg-gray-700/30',
          props.canManageRealtime ? 'cursor-pointer' : 'opacity-80 pointer-events-none'
        ]"
      >
        <div class="flex items-center min-w-0 mr-4">
          <div class="flex-shrink-0">
            <img v-if="user.avatarUrl" :src="user.avatarUrl" class="w-8 h-8 rounded-full object-cover border border-gray-200 dark:border-gray-700" :alt="user.name">
            <div v-else class="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 flex items-center justify-center font-bold text-xs tracking-wider">
              {{ getInitials(user.name) }}
            </div>
          </div>
          <div class="ml-3 min-w-0">
            <p class="text-xs font-semibold text-gray-900 dark:text-white truncate">
              {{ user.name }}
            </p>
            <p class="text-[10px] text-gray-500 dark:text-gray-400 truncate">
              {{ user.role === 'owner' ? 'Owner (Default)' : 'Shared Editor' }}
            </p>
          </div>
        </div>
        
        <div class="flex-shrink-0">
          <div 
            class="w-4 h-4 rounded-full border flex items-center justify-center transition-all"
            :class="[
              isActiveController(user.id)
                ? 'border-indigo-500 bg-indigo-500'
                : 'border-gray-300 dark:border-gray-600'
            ]"
          >
            <svg v-if="isActiveController(user.id)" class="w-2 h-2 text-white fill-current" viewBox="0 0 8 8">
              <circle cx="4" cy="4" r="3" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue';

const props = defineProps({
  sharedUsers: {
    type: Array,
    default: () => []
  },
  realtimeUserId: {
    type: String,
    default: null
  },
  playlistOwnerId: {
    type: String,
    default: ''
  },
  playlistOwnerName: {
    type: String,
    default: 'Owner'
  },
  playlistOwnerAvatarUrl: {
    type: String,
    default: null
  },
  canManageRealtime: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['change-realtime-user']);

const eligibleUsers = computed(() => {
  const list = [];
  
  // Owner is always eligible and at the top
  list.push({
    id: props.playlistOwnerId,
    name: props.playlistOwnerName || 'Owner',
    role: 'owner',
    avatarUrl: props.playlistOwnerAvatarUrl
  });
  
  // Shared users with role 'editor' are eligible
  props.sharedUsers.forEach(user => {
    if (user.role === 'editor') {
      const displayName = user.name || user.fullName || user.full_name || user.username || user.email || 'Shared Editor';
      list.push({
        id: user.id || user._id,
        name: displayName,
        role: 'editor',
        avatarUrl: user.avatarUrl || user.avatar_url
      });
    }
  });
  
  return list;
});

const isActiveController = (userId) => {
  if (!props.realtimeUserId || String(props.realtimeUserId) === String(props.playlistOwnerId)) {
    return String(userId) === String(props.playlistOwnerId);
  }
  return String(props.realtimeUserId) === String(userId);
};

const selectController = (userId) => {
  console.log('RealtimeControlSelector selectController called with userId:', userId, 'props.playlistOwnerId:', props.playlistOwnerId);
  if (!props.canManageRealtime) return;
  
  // If selecting owner, we pass null to reset back to default owner control
  const designatedId = String(userId) === String(props.playlistOwnerId) ? null : userId;
  console.log('RealtimeControlSelector selectController computed designatedId:', designatedId);
  emit('change-realtime-user', designatedId);
};

const getInitials = (name) => {
  if (!name || name === 'Shared Editor' || name === 'Owner') return name === 'Owner' ? 'OW' : 'ED';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};
</script>
